import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  fetchPublicationsData, 
  groupPublicationsByYear, 
  getPublicationById 
} from '../services/publicationsService';
import PublicationCard from '../components/PublicationCard';
import PDFViewer from '../components/PDFViewer';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';
import FloatingShareButton from '../components/FloatingShareButton';
import { metaTagsService } from '../services/metaTagsService';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * Page for displaying all publications grouped by year with filtering
 * @returns {JSX.Element}
 */
const PublicationsPage = () => {
  const { publicationId } = useParams(); // For individual publication view
  const { theme, config, loading: configLoading, error: configError } = useConfig();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const isMobileView = useMediaQuery('(max-width: 768px)');

  // Load publications data
  useEffect(() => {
    const loadPublications = async () => {
      try {
        setLoading(true);
        setError(null);

        const sheetId = config?.publications_sheet_id;
        console.log('Loading publications data...', { sheetId });
        const publicationsData = sheetId ? await fetchPublicationsData(sheetId) : [];
        console.log('Loaded publications:', publicationsData);

        if (!Array.isArray(publicationsData) || publicationsData.length === 0) {
          setError('No publications found');
        } else {
          setPublications(publicationsData);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading publications:', err);
        setError('Failed to load publications. Please try again later.');
        setLoading(false);
      }
    };

    if (!configLoading) {
      loadPublications();
    }
  }, [config, configLoading]);

  // Handle individual publication view from URL
  useEffect(() => {
    if (publicationId && publications.length > 0) {
      const publication = getPublicationById(publications, publicationId);
      if (publication) {
        setSelectedPublication(publication);
        setShowPDFViewer(true);
      }
    }
  }, [publicationId, publications]);

  // Update meta tags when component mounts or config changes
  useEffect(() => {
    if (config && !configLoading) {
      const logoUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
      
      metaTagsService.updateMetaTags({
        title: `Publications - ${config.name || 'Penta University'}`,
        description: 'Explore our collection of books, articles, and educational materials',
        image: logoUrl,
        url: `${window.location.origin}/publications`,
        type: 'website'
      });
    }

    // Cleanup function to reset meta tags when component unmounts
    return () => {
      if (config) {
        metaTagsService.resetToDefaults();
      }
    };
  }, [config, configLoading]);

  // Handle publication click
  const handlePublicationClick = (publication) => {
    setSelectedPublication(publication);
    setShowPDFViewer(true);
  };

  // Close PDF viewer
  const closePDFViewer = () => {
    setShowPDFViewer(false);
    setSelectedPublication(null);
  };

  // Determine which layout to use based on device type
  const Layout = isMobile() ? MobileLayout : DesktopLayout;

  // Handle config loading/error state
  if (configLoading) {
    return <p>Loading configuration...</p>;
  }

  if (configError) {
    return <p>Error loading configuration: {configError.message || configError}</p>;
  }

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: theme.colors.primary
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              Loading publications...
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              margin: '0 auto',
              border: `4px solid ${theme.colors.lightGray}`,
              borderTopColor: theme.colors.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Group publications by year (already sorted by year in the service)
  const groupedPublications = groupPublicationsByYear(publications);

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Page header */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h1 style={{
            fontSize: isMobile() ? '1.75rem' : '2.5rem',
            fontWeight: 'bold',
            marginBottom: '16px',
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            letterSpacing: '-0.5px'
          }}>
            Publications
          </h1>

          <p style={{ 
            color: theme.colors.text, 
            fontSize: isMobile() ? '1rem' : '1.2rem',
            fontWeight: '400',
            fontFamily: theme.fonts.body,
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: '1.6',
            padding: isMobile() ? '0 15px' : '0'
          }}>
            Educational resources and documentation from our lecture series
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: theme.colors.error,
            backgroundColor: '#FFEEEE',
            borderRadius: theme.borderRadius.medium,
            marginBottom: '24px'
          }}>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                marginTop: '16px'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Publications grouped by year with horizontal scrolling */}
        {!error && Object.keys(groupedPublications).length > 0 && (
          <div>
            {Object.entries(groupedPublications).map(([year, yearPublications]) => (
              <div key={year} style={{ marginBottom: '60px' }}>
                {/* Year header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: isMobileView ? '20px' : '30px',
                  borderBottom: `2px solid ${theme.colors.lightGray}`,
                  paddingBottom: '15px'
                }}>
                  <h2 style={{
                    fontSize: isMobileView ? '24px' : '32px',
                    fontWeight: 'bold',
                    color: theme.colors.primary,
                    margin: 0,
                    marginRight: '20px'
                  }}>
                    {year}
                  </h2>
                  <div style={{
                    fontSize: isMobileView ? '14px' : '16px',
                    color: theme.colors.darkGray,
                    fontWeight: '500'
                  }}>
                    {yearPublications.length} {yearPublications.length === 1 ? 'publication' : 'publications'}
                  </div>
                </div>

                {/* Horizontal scrolling container */}
                <div style={{ position: 'relative' }}>
                  {/* Scroll buttons */}
                  {yearPublications.length > (isMobile() ? 1 : 3) && (
                    <>
                      <button
                        onClick={() => {
                          const container = document.getElementById(`pub-scroll-${year}`);
                          if (container) {
                            container.scrollBy({ left: -320, behavior: 'smooth' });
                          }
                        }}
                        style={{
                          position: 'absolute',
                          left: isMobileView ? '-10px' : '-20px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          width: isMobileView ? '30px' : '40px',
                          height: isMobileView ? '30px' : '40px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: isMobileView ? '16px' : '20px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ←
                      </button>
                      
                      <button
                        onClick={() => {
                          const container = document.getElementById(`pub-scroll-${year}`);
                          if (container) {
                            container.scrollBy({ left: 320, behavior: 'smooth' });
                          }
                        }}
                        style={{
                          position: 'absolute',
                          right: isMobileView ? '-10px' : '-20px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          width: isMobileView ? '30px' : '40px',
                          height: isMobileView ? '30px' : '40px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: isMobileView ? '16px' : '20px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        →
                      </button>
                    </>
                  )}

                  {/* Publications horizontal scroll */}
                  <div
                    id={`pub-scroll-${year}`}
                    style={{
                      display: 'flex',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      scrollBehavior: 'smooth',
                      gap: '24px',
                      padding: '8px 4px',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitScrollbar: 'none'
                    }}
                  >
                    {yearPublications.map(publication => (
                      <div 
                        key={publication.id} 
                        style={{
                          flex: isMobileView ? '0 0 250px' : '0 0 300px',
                          minWidth: isMobileView ? '250px' : '300px',
                          maxWidth: isMobileView ? '250px' : '300px'
                        }}
                      >
                        <PublicationCard 
                          publication={publication}
                          onClick={handlePublicationClick}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!error && publications.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '80px 40px',
            color: theme.colors.darkGray
          }}>
            <h3 style={{ 
              marginBottom: '16px',
              fontSize: '24px',
              fontWeight: '300',
              color: theme.colors.text
            }}>
              No publications available yet
            </h3>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.6'
            }}>
              Check back soon for educational resources and documentation.
            </p>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {showPDFViewer && selectedPublication && (
        <PDFViewer
          pdfUrl={selectedPublication.pdfUrl}
          title={selectedPublication.title}
          author={selectedPublication.author}
          onClose={closePDFViewer}
        />
      )}
      <FloatingShareButton 
        pageTitle="Publications Archive"
        pageDescription="Browse our collection of publications and resources"
      />
    </Layout>
  );
};

export default PublicationsPage;